import {compact} from '@benchling/js-utils';

import {assert} from 'Asserts';
import {ProcessFlowchartNodeDetailsType} from 'graphql-client/__generated/graphqlGlobalTypes';
import findFirstMatchingNodeAlongEveryPath from 'util/graph-utils/findFirstMatchingNodeAlongEveryPath';
import {AdjacencyMap} from 'util/graph-utils/GraphUtilConstants';
import {assertNever} from 'util/Types';

import getSchemaMapperConfigsMappingToNode from '../util/getSchemaMapperConfigsMappingToNode';
import {
  ProcessFlowchartForValidation,
  ProcessGraphBuilderEdge,
  ProcessGraphBuilderNode,
  ProcessGraphBuilderNodeTemplate,
  ProcessGraphBuilderOutputNode,
  ProcessGraphBuilderRootNode,
  ProcessGraphBuilderTaskNode,
  SchemaMapperConfig,
  SchemaMapperConfigWithMapperData,
} from './ProcessGraphBuilderConstants';

export function getTemplateNodeFromClientId(
  nodes: Array<ProcessGraphBuilderNodeTemplate>,
  clientId: string
): ProcessGraphBuilderNodeTemplate | null {
  return nodes.find((tNode) => tNode.clientId === clientId) || null;
}

export function getTemplateNodeFromSchemaId(
  nodes: Array<ProcessGraphBuilderNodeTemplate>,
  schemaId: string
): ProcessGraphBuilderNodeTemplate | null {
  return nodes.find((tNode) => tNode.schemaId === schemaId) || null;
}

export function getSchemaIdFromNodeClientId(
  flowchart: ProcessFlowchartForValidation,
  clientId: string
): string | null {
  const node = getFlowchartNodeFromClientId(flowchart, clientId);
  if (node == null) {
    return null;
  }

  switch (node.type) {
    case ProcessFlowchartNodeDetailsType.TASK_NODE_DETAILS:
    case ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS:
    case ProcessFlowchartNodeDetailsType.OUTPUT_NODE_DETAILS:
      return node.taskSchemaId;
    case ProcessFlowchartNodeDetailsType.ROUTER_NODE_DETAILS:
    case ProcessFlowchartNodeDetailsType.END_NODE_DETAILS:
      return null;
    default:
      assert(false, `Invalid node type ${assertNever(node)}`);
      return null;
  }
}

export function getNodesByClientId(
  flowchart: ProcessFlowchartForValidation
): Record<string, ProcessGraphBuilderNode> {
  const nodeById: Record<string, ProcessGraphBuilderNode> = {};
  for (const node of flowchart.nodes) {
    nodeById[node.clientId] = node;
  }
  return nodeById;
}

export function getAdjacencyMapFromProcessFlowchart(flowchart: ProcessFlowchartForValidation): AdjacencyMap {
  const adjacencyMap: AdjacencyMap = {};
  for (const node of flowchart.nodes) {
    adjacencyMap[node.clientId] = [];
  }
  for (const edge of flowchart.edges) {
    if (adjacencyMap[edge.fromNodeId] == null) {
      adjacencyMap[edge.fromNodeId] = [];
    }
    adjacencyMap[edge.fromNodeId].push(edge.toNodeId);
    if (adjacencyMap[edge.toNodeId] == null) {
      adjacencyMap[edge.toNodeId] = [];
    }
  }
  return adjacencyMap;
}

/**
 * Gets the closest downstream task nodes, accounting for chained routers.
 * Returns multiple task nodes when node branches into multiple task nodes or leads to a router node that eventually branches into multiple task nodes.
 */
export function getTaskNodesDownstreamOfNode(
  nodeClientId: string,
  flowchart: ProcessFlowchartForValidation
): Array<ProcessGraphBuilderTaskNode | ProcessGraphBuilderOutputNode> {
  const adjacencyMap: AdjacencyMap = {};
  const taskNodeById: Record<string, ProcessGraphBuilderTaskNode | ProcessGraphBuilderOutputNode> = {};
  for (const node of flowchart.nodes) {
    adjacencyMap[node.clientId] = [];
    if (
      node.type === ProcessFlowchartNodeDetailsType.TASK_NODE_DETAILS ||
      node.type === ProcessFlowchartNodeDetailsType.OUTPUT_NODE_DETAILS
    ) {
      taskNodeById[node.clientId] = node;
    }
  }
  for (const edge of flowchart.edges) {
    adjacencyMap[edge.fromNodeId].push(edge.toNodeId);
  }

  return findFirstMatchingNodeAlongEveryPath(nodeClientId, adjacencyMap, (nodeId) =>
    Object.keys(taskNodeById).includes(nodeId)
  ).map(({nodeId}) => taskNodeById[nodeId]);
}

/**
 * Gets the most recent task node ancestors of a node, accounting for multiple incoming edges
 * and chained routers.
 */
export function getTaskNodesUpstreamOfNode(
  nodeClientId: string,
  flowchart: ProcessFlowchartForValidation
): Array<ProcessGraphBuilderTaskNode | ProcessGraphBuilderRootNode> {
  const invertedAdjacencyMap: AdjacencyMap = {};
  const taskNodeById: Record<string, ProcessGraphBuilderTaskNode | ProcessGraphBuilderRootNode> = {};
  for (const node of flowchart.nodes) {
    invertedAdjacencyMap[node.clientId] = [];
    if (
      node.type === ProcessFlowchartNodeDetailsType.TASK_NODE_DETAILS ||
      node.type === ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS
    ) {
      taskNodeById[node.clientId] = node;
    }
  }
  for (const edge of flowchart.edges) {
    if (invertedAdjacencyMap[edge.toNodeId] == null) {
      invertedAdjacencyMap[edge.toNodeId] = [];
    }
    invertedAdjacencyMap[edge.toNodeId].push(edge.fromNodeId);
  }

  return findFirstMatchingNodeAlongEveryPath(nodeClientId, invertedAdjacencyMap, (nodeId) =>
    Object.keys(taskNodeById).includes(nodeId)
  ).map(({nodeId}) => taskNodeById[nodeId]);
}

export function getSchemaIdMappingToNode(
  node: ProcessGraphBuilderTaskNode | ProcessGraphBuilderOutputNode,
  upstreamNode: ProcessGraphBuilderTaskNode | ProcessGraphBuilderRootNode,
  schemaMapperConfigs: Array<SchemaMapperConfigWithMapperData>,
  mapperConfigByClientId: {[clientId: string]: SchemaMapperConfigWithMapperData}
): string | null {
  const mapperConfigsForNode = getSchemaMapperConfigsMappingToNode(
    node,
    new Set([upstreamNode.taskSchemaId]),
    schemaMapperConfigs.map(({clientId, fromTaskSchemas, toTaskSchema}) => ({
      clientId,
      fromTaskSchemaIds: fromTaskSchemas.map(({id}) => id),
      toTaskSchemaId: toTaskSchema.id,
    }))
  );
  if (mapperConfigsForNode?.length !== 1) {
    return null;
  }

  const fromSchemaIds = mapperConfigByClientId[
    mapperConfigsForNode[0].clientId
  ].mapperConfig.schemaFieldMapperConfigs.map(({fromSchemaId}) => fromSchemaId);
  if (new Set(fromSchemaIds).size !== 1) {
    return null;
  }
  return fromSchemaIds[0];
}

export function getFlowchartNodeFromClientId(
  flowchart: ProcessFlowchartForValidation,
  clientId: string
): ProcessGraphBuilderNode | undefined {
  return flowchart.nodes.find((node) => node.clientId === clientId);
}

export function getInboundEdges(
  node: ProcessGraphBuilderNode,
  edges: Array<ProcessGraphBuilderEdge>
): Array<ProcessGraphBuilderEdge> {
  return edges.filter(({toNodeId}) => toNodeId === node.clientId);
}

export function getOutboundEdges(
  node: ProcessGraphBuilderNode,
  edges: Array<ProcessGraphBuilderEdge>
): Array<ProcessGraphBuilderEdge> {
  return edges.filter(({fromNodeId}) => fromNodeId === node.clientId);
}

export function getEdgeDisplayName(
  edge: ProcessGraphBuilderEdge,
  flowchart: ProcessFlowchartForValidation
): string {
  const fromNode = getFlowchartNodeFromClientId(flowchart, edge.fromNodeId);
  const toNode = getFlowchartNodeFromClientId(flowchart, edge.toNodeId);

  return fromNode == null || toNode == null ? edge.clientId : `${fromNode.name} - ${toNode.name}`;
}

export function getFulfillingMapperConfigs(
  node: ProcessGraphBuilderNode,
  flowchart: ProcessFlowchartForValidation,
  templateNode: ProcessGraphBuilderNodeTemplate,
  incomingEdges: Array<ProcessGraphBuilderEdge>
): Array<SchemaMapperConfig> | null {
  if (node.type === ProcessFlowchartNodeDetailsType.END_NODE_DETAILS) {
    return null;
  }
  // Only task and output nodes can be mapped to. All other node types cannot have any mappers configured so we
  // return null.
  if (
    node.type !== ProcessFlowchartNodeDetailsType.TASK_NODE_DETAILS &&
    node.type !== ProcessFlowchartNodeDetailsType.OUTPUT_NODE_DETAILS
  ) {
    return null;
  }

  const inboundSchemas = new Set(
    compact(
      getTaskNodesUpstreamOfNode(node.clientId, flowchart).map(({clientId}) =>
        getSchemaIdFromNodeClientId(flowchart, clientId)
      )
    )
  );

  return getSchemaMapperConfigsMappingToNode(node, inboundSchemas, templateNode.inboundMapperConfigs);
}
